<template>
  <div class="claim_overlay">
    <div
      class="claim_popup"
      role="dialog"
      aria-modal="true"
      aria-labelledby="offer_detail_title"
    >
      <div class="claim_header" id="offer_detail_title">優惠券詳情</div>
      <div class="claim_body">
        <div class="claim_loading" v-if="isLoading">
          <div class="claim_spinner"></div>
          載入中…
        </div>
        <template v-else-if="getOfferDetail">
          <div class="coupon_hero">
            <span class="coupon_badge">優惠活動</span>
            <h3 class="coupon_title">{{ getOfferDetail.title }}</h3>
          </div>
          <div class="coupon_detail_card">
            <template v-if="getOfferDetail.description">
              <h4>優惠說明</h4>
              <p>{{ getOfferDetail.description }}</p>
            </template>
            <h4>使用門檻</h4>
            <p
              v-for="(text, index) in getPublicRuleDetail.thresholdTexts"
              :key="`threshold_${index}`"
            >
              {{ text }}
            </p>
            <h4>優惠內容</h4>
            <p
              v-for="(text, index) in getPublicRuleDetail.benefitTexts"
              :key="`benefit_${index}`"
            >
              {{ text }}
            </p>
            <h4>領取期間</h4>
            <p>{{ getClaimPeriodText }}</p>
            <template v-if="getValidityText">
              <h4>使用期限</h4>
              <p>{{ getValidityText }}</p>
            </template>
          </div>
        </template>
        <p v-else class="claim_message claim_invalid">{{ getLoadError }}</p>
      </div>
      <div class="claim_footer">
        <button
          v-if="isLoadFailed"
          type="button"
          class="button_secondary"
          @click="close"
        >
          關閉
        </button>
        <button
          v-else
          type="button"
          class="button_primary"
          :disabled="!canStartLogin"
          @click="startLogin"
        >
          登入後領取優惠券
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getClaimOfferUuid } from "@/utils/couponClaimLink.utils";
import { COUPON_DATE_FORMAT, formatCouponDateTime } from "@/utils/coupon.utils";
import {
  getCouponPublicRuleDetail,
  getTemplateValidityText,
} from "@/utils/couponTemplate.utils";

export default {
  name: "CouponOfferDetailPopup",
  props: {
    offerUuid: String,
    isLoginStarting: { type: Boolean, default: false },
  },
  emits: ["login", "close"],
  data() {
    return {
      isLoadingDetail: Boolean(getClaimOfferUuid(this.offerUuid)),
      loadError: "",
    };
  },
  computed: {
    getOfferDetailState() {
      const detail = this.$store.getters["coupon/couponOfferDetail"];
      return detail?.uuid === this.offerUuid ? detail : null;
    },
    getOfferDetail() {
      return this.getOfferDetailState?.data || null;
    },
    isLoading() {
      return this.isLoadingDetail || Boolean(this.getOfferDetailState?.loading);
    },
    isLoadFailed() {
      return !this.isLoading && !this.getOfferDetail;
    },
    canStartLogin() {
      return (
        Boolean(this.getOfferDetail) && !this.isLoading && !this.isLoginStarting
      );
    },
    getLoadError() {
      return (
        this.getOfferDetailState?.error ||
        this.loadError ||
        "此領券連結已失效或無法使用"
      );
    },
    getPublicRuleDetail() {
      // 匿名頁不使用其他店家的菜單快取，只展示公開規則與指定目標泛稱。
      return getCouponPublicRuleDetail({
        publicRules: this.getOfferDetail?.public_rules,
      });
    },
    getClaimPeriodText() {
      const detail = this.getOfferDetail;
      const begin = formatCouponDateTime(
        detail?.claim_begin_at,
        detail?.timezone_name,
        COUPON_DATE_FORMAT
      );
      const end = formatCouponDateTime(
        detail?.claim_end_at,
        detail?.timezone_name,
        COUPON_DATE_FORMAT
      );
      return `${begin} ～ ${end}`;
    },
    getValidityText() {
      return getTemplateValidityText(this.getOfferDetail?.public_rules, this.getOfferDetail?.timezone_name);
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    if (this.getOfferDetailState)
      this.$store.dispatch("coupon/clearV2Detail", "offerDetail");
  },
  methods: {
    async load() {
      if (!getClaimOfferUuid(this.offerUuid)) return;
      try {
        await this.$store.dispatch(
          "coupon/fetchCouponOfferDetail",
          this.offerUuid
        );
      } catch (error) {
        this.loadError = error?.message || "查詢失敗，請稍後再試";
      } finally {
        this.isLoadingDetail = false;
      }
    },
    startLogin() {
      if (this.canStartLogin) this.$emit("login");
    },
    close() {
      if (!this.isLoginStarting) this.$emit("close");
    },
  },
};
</script>

<style lang="scss" scoped>
.claim_overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
}

.claim_popup {
  width: min(100%, 360px);
  max-height: 86vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #f6f6f6;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.claim_header {
  padding: 18px 16px 4px;
  color: #fd5001;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
}

.claim_body {
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 4px;
}

.coupon_hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 12px 14px 0;
  padding: 16px 15px;
  background-color: #fff4e7;
  border: 1px solid #f1d9bb;
  border-radius: 10px;
}

.coupon_badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 4px;
  background-color: #fff2e8;
  color: #9b4e1b;
  font-size: 15px;
  white-space: nowrap;
}

.coupon_title {
  flex: 1;
  min-width: 0;
  margin: 0;
  color: #292929;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.coupon_detail_card {
  margin: 12px 14px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  color: #292929;
  font-size: 14px;
  line-height: 1.6;

  h4 {
    margin: 14px 0 4px;
    color: #8b7563;
    font-size: 12px;
    font-weight: 500;
  }

  h4:first-child {
    margin-top: 0;
  }

  p {
    margin: 4px 0;
    overflow-wrap: anywhere;
  }
}

.claim_message {
  margin: 4px 14px 0;
  color: #d93025;
  font-size: 14px;
  text-align: center;
  overflow-wrap: anywhere;
}

.claim_invalid {
  margin: 28px 14px 12px;
}

.claim_loading {
  padding: 40px 0;
  color: #666;
  font-size: 14px;
  text-align: center;
}

.claim_spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto 12px;
  border: 4px solid #e5e5e5;
  border-top-color: #fd5001;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.claim_footer {
  display: flex;
  gap: 10px;
  padding: 12px 14px 16px;

  button {
    flex: 1;
    min-width: 0;
    padding: 12px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.5;
  }
}

.button_secondary {
  background-color: #fff;
  border: 1px solid #ccc;
  color: #4d4d4d;
}

.button_outline {
  background-color: #fff;
  border: 1px solid #fd5001;
  color: #fd5001;
}

.button_primary {
  background-color: #06c755;
  border: 0;
  color: #fff;
}

.button_primary:disabled,
.button_secondary:disabled {
  background-color: #ddd;
  color: #777;
}
</style>
